import * as THREE from 'three';
import { 
    WORLD_CHUNKS_ACROSS, 
    CHUNK_SIZE,
    WATER_LEVEL,
    BASE_HEIGHT,
    SPAWN_CONFIG,
    ISLAND_RADIUS
} from './TerrainConstants.js';
import { createSpawnOptimizer } from './SpawnOptimizer.js';

/**
 * Manages player spawn position logic based on terrain
 * Responsible for:
 * - Finding valid spawn positions on terrain
 * - Ensuring spawn points are on walkable surfaces
 * - Handling terrain-specific spawn requirements
 */
export class PlayerSpawnManager {
    constructor(mode = SPAWN_CONFIG.MODE.RANDOM) {
        this.mode = mode;
        this.currentTestPointIndex = 0;
        this.PLAYER_HEIGHT = 2.0; // Height of player in world units
        
        // Create spawn optimizer
        this.spawnOptimizer = createSpawnOptimizer({
            waterLevel: WATER_LEVEL,
            minHeightAboveWater: 2,
            maxSlope: 0.3
        });
    }

    /**
     * Gets a valid spawn position on the terrain
     * @param {Object} chunkManager - The chunk manager instance
     * @param {Function} getTerrainHeight - Function to get terrain height at coordinates
     * @returns {THREE.Vector3} Spawn position
     */
    getSpawnPosition(chunkManager, getTerrainHeight) {
        console.log('[PlayerSpawnManager] Getting spawn position with instant gameplay mode');
        
        // For instant gameplay, always spawn at center of the world (center of chunk 0,0)
        // This is where we created our flat safe area
        const chunkSize = CHUNK_SIZE;
        const spawnX = chunkSize / 2;  // Center of chunk 0,0
        const spawnZ = chunkSize / 2;
        
        // Get height at spawn point
        const terrainHeight = getTerrainHeight(spawnX, spawnZ);
        console.log(`[PlayerSpawnManager] Center spawn at (${spawnX}, ${spawnZ}) with height ${terrainHeight}`);
        
        // Return spawn position with player height offset
        return new THREE.Vector3(
            spawnX,
            terrainHeight + this.PLAYER_HEIGHT,
            spawnZ
        );
    }

    /**
     * Gets a fixed spawn point from the configuration
     */
    getFixedSpawnPoint(chunkManager, getTerrainHeight) {
        const point = SPAWN_CONFIG.FIXED_POINTS[0]; // Use first fixed point
        const terrainHeight = getTerrainHeight(point.x, point.z);
        return new THREE.Vector3(
            point.x,
            Math.max(terrainHeight + this.PLAYER_HEIGHT, WATER_LEVEL + this.PLAYER_HEIGHT + 1),
            point.z
        );
    }

    /**
     * Gets a test spawn point from the configuration
     */
    getTestSpawnPoint(chunkManager, getTerrainHeight) {
        const point = SPAWN_CONFIG.TEST_POINTS[this.currentTestPointIndex];
        const terrainHeight = getTerrainHeight(point.x, point.z);
        
        // Cycle to next test point
        this.currentTestPointIndex = (this.currentTestPointIndex + 1) % SPAWN_CONFIG.TEST_POINTS.length;
        
        console.log(`Using test spawn point: ${point.description || 'unnamed'}`);
        return new THREE.Vector3(
            point.x,
            Math.max(terrainHeight + this.PLAYER_HEIGHT, WATER_LEVEL + this.PLAYER_HEIGHT + 1),
            point.z
        );
    }

    /**
     * Gets a random spawn point within constraints
     */
    getRandomSpawnPoint(chunkManager, getTerrainHeight) {
        const centerX = Math.floor(WORLD_CHUNKS_ACROSS / 2) * CHUNK_SIZE;
        const centerZ = Math.floor(WORLD_CHUNKS_ACROSS / 2) * CHUNK_SIZE;
        const maxAttempts = 50;
        let attempts = 0;

        while (attempts < maxAttempts) {
            // Generate random angle and distance within constraints
            const angle = Math.random() * Math.PI * 2;
            const distance = ISLAND_RADIUS * (
                SPAWN_CONFIG.RANDOM_CONSTRAINTS.MIN_DISTANCE_FROM_CENTER +
                Math.random() * (SPAWN_CONFIG.RANDOM_CONSTRAINTS.MAX_DISTANCE_FROM_CENTER - 
                               SPAWN_CONFIG.RANDOM_CONSTRAINTS.MIN_DISTANCE_FROM_CENTER)
            );

            const x = centerX + Math.cos(angle) * distance;
            const z = centerZ + Math.sin(angle) * distance;
            const terrainHeight = getTerrainHeight(x, z);

            // Check if this is a valid spawn point
            if (this.isValidSpawnPosition(new THREE.Vector3(x, terrainHeight, z), getTerrainHeight)) {
                return new THREE.Vector3(
                    x,
                    Math.max(terrainHeight + this.PLAYER_HEIGHT, WATER_LEVEL + this.PLAYER_HEIGHT + 1),
                    z
                );
            }

            attempts++;
        }

        // Fallback to center if no valid point found
        console.warn('No valid random spawn point found, using center');
        return this.getFixedSpawnPoint(chunkManager, getTerrainHeight);
    }

    /**
     * Validates a spawn position
     */
    isValidSpawnPosition(position, getTerrainHeight) {
        // First use our optimizer to check if this is a good spawn point
        const isOptimal = this.spawnOptimizer.isGoodSpawnPoint(
            { x: position.x, z: position.z },
            getTerrainHeight,
            WATER_LEVEL
        );
        
        // If optimizer says it's good, return true
        if (isOptimal) return true;
        
        // Otherwise, use the old method as fallback
        const terrainHeight = getTerrainHeight(position.x, position.z);
        
        // Check if position is above water with enough clearance
        if (terrainHeight < WATER_LEVEL + SPAWN_CONFIG.RANDOM_CONSTRAINTS.MIN_HEIGHT) return false;
        
        // Check if position is on a relatively flat surface
        const radius = 2; // Check surrounding points
        let totalHeight = 0;
        let validPoints = 0;
        
        for (let x = -radius; x <= radius; x++) {
            for (let z = -radius; z <= radius; z++) {
                const checkHeight = getTerrainHeight(position.x + x, position.z + z);
                if (checkHeight === undefined || checkHeight === null) continue;
                
                totalHeight += checkHeight;
                validPoints++;
                
                // Check for steep slopes
                if (Math.abs(checkHeight - terrainHeight) > SPAWN_CONFIG.RANDOM_CONSTRAINTS.MAX_SLOPE) {
                    return false; // Too steep
                }
            }
        }
        
        // Ensure we have enough valid points to check
        if (validPoints < 4) return false;
        
        // Check if the average height is consistent
        const averageHeight = totalHeight / validPoints;
        if (Math.abs(averageHeight - terrainHeight) > 1.0) return false;
        
        return true;
    }

    /**
     * Sets the spawn mode
     * @param {string} mode - One of SPAWN_CONFIG.MODE values
     */
    setSpawnMode(mode) {
        if (Object.values(SPAWN_CONFIG.MODE).includes(mode)) {
            this.mode = mode;
            this.currentTestPointIndex = 0;
        } else {
            console.warn(`Invalid spawn mode: ${mode}`);
        }
    }
} 